import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { Spinner } from '@heroui/react';
import { auth, refreshUser } from '../../../lib/firebase';
import { refreshUserDoc, useAuth } from '../../../hooks/useAuth';
import { translateFirebaseErrorObject } from '../../../utils/firebaseErrors';
import { getPostAuthPath } from '../utils/post-auth-redirect';

function ActionLoading({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
      <Spinner size="lg" />
      <p className="text-muted text-sm">{message}</p>
    </div>
  );
}

export default function EmailActionPage() {
  const [searchParams] = useSearchParams();
  const { authUser, userDoc, loading, setAuthUser } = useAuth();
  const [processing, setProcessing] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    let cancelled = false;

    const processAction = async () => {
      if (!oobCode) {
        if (!cancelled) {
          setError('Código de ação inválido');
          setProcessing(false);
        }
        return;
      }

      try {
        if (mode === 'verifyEmail') {
          await applyActionCode(auth, oobCode);
          const current = auth.currentUser;
          if (current) {
            const refreshed = await refreshUser(current);
            if (!cancelled) setAuthUser(refreshed);
          }
          if (!cancelled) setSuccess(true);
        } else if (mode === 'resetPassword') {
          localStorage.removeItem('firebase:authUser');
          sessionStorage.clear();
          if (!cancelled) setSuccess(true);
        } else if (!cancelled) {
          setError('Ação não reconhecida');
        }
      } catch (err) {
        if (!cancelled) {
          setError(translateFirebaseErrorObject(err));
        }
      } finally {
        if (!cancelled) setProcessing(false);
      }
    };

    const timer = setTimeout(() => {
      void processAction();
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [mode, oobCode, setAuthUser]);

  useEffect(() => {
    if (mode !== 'verifyEmail' || !success || !authUser || userDoc || loading) return;
    void refreshUserDoc();
  }, [mode, success, authUser, userDoc, loading]);

  if (mode === 'resetPassword') {
    if (processing) {
      return <ActionLoading message="Processando reset de senha..." />;
    }
    if (error) {
      return <Navigate to={`/login?error=${encodeURIComponent(error)}`} replace />;
    }
    if (success && oobCode) {
      return <Navigate to={`/reset-password?oobCode=${oobCode}&t=${Date.now()}`} replace />;
    }
  }

  if (processing) {
    return <ActionLoading message="Processando verificação de email..." />;
  }

  if (error) {
    if (authUser) {
      return <Navigate to={`/verify-email?error=${encodeURIComponent(error)}`} replace />;
    }
    return <Navigate to={`/login?error=${encodeURIComponent(error)}`} replace />;
  }

  if (mode === 'verifyEmail' && success) {
    if (!authUser) {
      return <Navigate to="/login?verified=true" replace />;
    }

    if (loading || !userDoc) {
      return <ActionLoading message="Atualizando estado..." />;
    }

    return <Navigate to={getPostAuthPath(userDoc)} replace />;
  }

  if (loading) {
    return <ActionLoading message="Carregando..." />;
  }

  return <Navigate to="/login" replace />;
}
